// IngestProgress — streaming progress UI for the staged ingest pipeline.
//
// v3: editorial vertical timeline. A single quiet line, each stage a node.
// Users sit here 10-30 min — design rewards patience, not urgency.

import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2, MinusCircle, AlertTriangle, Sparkles, Clock } from 'lucide-react';
import { useT } from '../i18n/index.js';
import type { DictionaryKey } from '@examforge/shared/i18n';
import type { LintReport } from '@examforge/shared/schemas';
import type { StageId } from '../lib/staged-ingest-client.js';
import { STAGE_IDS } from '../lib/staged-ingest-client.js';
import { LintSummary } from './lint-summary.js';

// ── Stage metadata ──────────────────────────────────────────────────────────

const STAGE_LABEL_KEYS: Record<StageId, DictionaryKey> = {
  parse_files: 'ingest.stage.parseFiles',
  bundle_past_papers: 'ingest.stage.bundlePastPapers',
  extract_cases: 'ingest.stage.extractCases',
  flashcards: 'ingest.stage.flashcards',
  mcqs: 'ingest.stage.mcqs',
  true_false: 'ingest.stage.trueFalse',
  essays: 'ingest.stage.essays',
  mindmaps: 'ingest.stage.mindmaps',
  glossary: 'ingest.stage.glossary',
  lecture_notes: 'ingest.stage.lectureNotes',
  proof_drills: 'ingest.stage.proofDrills',
  free_responses: 'ingest.stage.freeResponses',
  reading_passages: 'ingest.stage.readingPassages',
  mock_papers: 'ingest.stage.mockPapers',
  audit: 'ingest.stage.audit',
};

const STAGE_SKIP_KEYS: Record<StageId, DictionaryKey> = {
  parse_files: 'ingest.skipReason.noFiles',
  bundle_past_papers: 'ingest.skipReason.noPastPaper',
  extract_cases: 'ingest.skipReason.outlineLocked',
  flashcards: 'ingest.skipReason.componentDisabled',
  mcqs: 'ingest.skipReason.componentDisabled',
  true_false: 'ingest.skipReason.componentDisabled',
  essays: 'ingest.skipReason.componentDisabled',
  mindmaps: 'ingest.skipReason.componentDisabled',
  glossary: 'ingest.skipReason.componentDisabled',
  lecture_notes: 'ingest.skipReason.componentDisabled',
  proof_drills: 'ingest.skipReason.componentDisabled',
  free_responses: 'ingest.skipReason.componentDisabled',
  reading_passages: 'ingest.skipReason.componentDisabled',
  mock_papers: 'ingest.skipReason.noMockSpec',
  audit: 'ingest.statusSkipped',
};

const LLM_STAGE_IDS: ReadonlySet<StageId> = new Set<StageId>([
  'extract_cases', 'flashcards', 'mcqs', 'true_false',
  'essays', 'mindmaps', 'glossary', 'lecture_notes',
  'proof_drills', 'free_responses', 'reading_passages', 'mock_papers',
]);

// ── State types ──────────────────────────────────────────────────────────────

export type StageStatus = 'pending' | 'running' | 'done' | 'skipped' | 'error';

export interface IngestProgressState {
  stages: Record<StageId, StageStatus>;
  currentStage: StageId | null;
  doneCount: number;
  totalApplicable: number;
  errors: { stage: StageId; message: string }[];
  stageStarts: Partial<Record<StageId, number>>;
  stageDurations: Partial<Record<StageId, number>>;
  pipelineStartedAt: number | null;
  pipelineCompletedAt: number | null;
  lowYieldStages: StageId[];
}

export const initialProgress: IngestProgressState = {
  stages: Object.fromEntries(STAGE_IDS.map((s) => [s, 'pending'])) as Record<StageId, StageStatus>,
  currentStage: null,
  doneCount: 0,
  totalApplicable: STAGE_IDS.length,
  errors: [],
  stageStarts: {},
  stageDurations: {},
  pipelineStartedAt: null,
  pipelineCompletedAt: null,
  lowYieldStages: [],
};

export interface IngestProgressProps {
  state: IngestProgressState;
  onCancel?(): void;
  lintReport?: LintReport;
}

// ── Component ────────────────────────────────────────────────────────────────

export function IngestProgress({ state, onCancel, lintReport }: IngestProgressProps) {
  const t = useT();
  const percent = useMemo(() => {
    if (state.totalApplicable === 0) return 0;
    const raw = Math.round((state.doneCount / state.totalApplicable) * 100);
    if (raw >= 100 && !state.pipelineCompletedAt) return 99;
    return Math.min(100, raw);
  }, [state.doneCount, state.totalApplicable, state.pipelineCompletedAt]);

  const [, forceTick] = useState(0);
  useEffect(() => {
    if (state.pipelineCompletedAt) return;
    if (!state.currentStage && !state.pipelineStartedAt) return;
    const id = window.setInterval(() => forceTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [state.currentStage, state.pipelineCompletedAt, state.pipelineStartedAt]);

  const totalElapsed = state.pipelineStartedAt
    ? (state.pipelineCompletedAt ?? Date.now()) - state.pipelineStartedAt
    : 0;

  const hasRecoverableErrors = state.errors.length > 0 && !state.pipelineCompletedAt;

  return (
    <main className="ingest">
      {/* Header — title, progress bar, elapsed */}
      <header className="ingest-head">
        <h1 className="ingest-title">{t('ingest.title')}</h1>
        <div className="ingest-bar-row" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
          <div className="ingest-rail">
            <div className="ingest-rail-fill" style={{ width: `${percent}%` }} />
          </div>
          <span className="ingest-pct">{percent}%</span>
        </div>
        <p className="ingest-hint">{t('ingest.hint')}</p>
        {state.pipelineStartedAt && (
          <span className="ingest-elapsed">
            <Clock size={11} />
            {t('ingest.totalElapsed')} {formatElapsed(totalElapsed)}
          </span>
        )}
      </header>

      {/* Error banner */}
      {hasRecoverableErrors && (
        <div className="ingest-warn">
          <AlertTriangle size={13} />
          {state.errors.length === 1
            ? t('ingest.recoverableSingle')
            : t('ingest.recoverableMulti', { n: state.errors.length })}
        </div>
      )}

      {/* Timeline — vertical line with nodes */}
      <ol className="ingest-tl">
        {STAGE_IDS.map((id, idx) => {
          const status = state.stages[id];
          const duration = state.stageDurations[id];
          const liveElapsed = status === 'running' && state.stageStarts[id]
            ? Date.now() - state.stageStarts[id]!
            : undefined;
          const elapsed = liveElapsed ?? duration;
          const isLlm = LLM_STAGE_IDS.has(id);
          const isLast = idx === STAGE_IDS.length - 1;
          const isRunning = status === 'running';

          return (
            <li
              key={id}
              data-stage-id={id}
              className={`ingest-tl-node is-${status}`}
            >
              {/* The vertical line segment behind the node */}
              {!isLast && <span className="ingest-tl-line" />}

              {/* Node dot */}
              <span className="ingest-tl-dot" aria-hidden="true">
                {status === 'done' && <CheckCircle2 size={16} />}
                {isRunning && <Loader2 size={16} className="ingest-spin" />}
                {status === 'skipped' && <MinusCircle size={16} />}
                {status === 'error' && <AlertTriangle size={16} />}
                {status === 'pending' && <span className="ingest-tl-pending-dot" />}
              </span>

              {/* Content */}
              <span className="ingest-tl-body">
                <span className="ingest-tl-label">{t(STAGE_LABEL_KEYS[id])}</span>

                {/* Running: show live timer + AI badge */}
                {isRunning && (
                  <span className="ingest-tl-meta">
                    {isLlm && (
                      <span className="ingest-tl-ai"><Sparkles size={9} /> AI</span>
                    )}
                    {elapsed !== undefined && (
                      <span className="ingest-tl-time">{formatElapsed(elapsed)}</span>
                    )}
                  </span>
                )}

                {/* Done: show duration */}
                {status === 'done' && elapsed !== undefined && (
                  <span className="ingest-tl-meta">
                    {isLlm && (
                      <span className="ingest-tl-ai"><Sparkles size={9} /> AI</span>
                    )}
                    <span className="ingest-tl-time">{formatElapsed(elapsed)}</span>
                  </span>
                )}

                {/* Skipped reason */}
                {status === 'skipped' && (
                  <span className="ingest-tl-meta">
                    <span className="ingest-tl-skip">{t(STAGE_SKIP_KEYS[id])}</span>
                  </span>
                )}

                {/* Error */}
                {status === 'error' && (
                  <span className="ingest-tl-meta">
                    <span className="ingest-tl-err">{t('ingest.statusError')}</span>
                  </span>
                )}

                {/* Low yield */}
                {status === 'done' && state.lowYieldStages.includes(id) && (
                  <span className="ingest-tl-low" title={t('ingest.lowYieldTip')}>
                    <AlertTriangle size={10} /> {t('ingest.lowYield')}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ol>

      {/* Error details */}
      {state.errors.length > 0 && (
        <details className="ingest-errors">
          <summary>{t('ingest.errorsSummary')} ({state.errors.length})</summary>
          <ul>
            {state.errors.map((e, i) => (
              <li key={i}><strong>{t(STAGE_LABEL_KEYS[e.stage])}:</strong> {e.message}</li>
            ))}
          </ul>
        </details>
      )}

      {/* Lint summary */}
      {lintReport && state.pipelineCompletedAt && (
        <div className="ingest-lint">
          <LintSummary report={lintReport} defaultOpen={lintReport.verdict === 'low_conf'} />
        </div>
      )}

      {/* Low-yield banner */}
      {state.pipelineCompletedAt && state.lowYieldStages.length > 0 && (
        <div className="ingest-warn">
          <AlertTriangle size={13} />
          {state.lowYieldStages.length === 1
            ? t('ingest.lowYieldBannerSingle', { stage: t(STAGE_LABEL_KEYS[state.lowYieldStages[0]!]) })
            : t('ingest.lowYieldBannerMulti', { n: state.lowYieldStages.length })}
        </div>
      )}

      {onCancel && (
        <button type="button" className="btn-secondary" onClick={onCancel}>
          {t('common.cancel')}
        </button>
      )}
    </main>
  );
}

// ── Reducer (unchanged) ──────────────────────────────────────────────────────

export function reduceIngestEvent(
  state: IngestProgressState,
  event: import('../lib/staged-ingest-client.js').IngestEvent,
): IngestProgressState {
  const now = Date.now();

  // Defensive: silently skip events for stages not in the frontend STAGE_IDS
  // (e.g. `condense_per_kind` — server-side pre-stage, not user-visible).
  const isTrackedStage = event.type !== 'pipeline-start' && event.type !== 'pipeline-complete'
    ? ('stage' in event && !(STAGE_IDS as readonly string[]).includes(event.stage as string))
    : false;
  if (isTrackedStage) return state;

  switch (event.type) {
    case 'pipeline-start': {
      return {
        ...initialProgress,
        totalApplicable: event.total,
        pipelineStartedAt: now,
      };
    }
    case 'stage-start': {
      return {
        ...state,
        stages: { ...state.stages, [event.stage]: 'running' },
        currentStage: event.stage,
        stageStarts: { ...state.stageStarts, [event.stage]: now },
      };
    }
    case 'stage-skip': {
      return {
        ...state,
        stages: { ...state.stages, [event.stage]: 'skipped' },
        totalApplicable: Math.max(0, state.totalApplicable - 1),
      };
    }
    case 'stage-done': {
      const startedAt = state.stageStarts[event.stage];
      const duration = startedAt ? now - startedAt : undefined;
      const prevStatus = state.stages[event.stage];
      const wasSkipped = prevStatus === 'skipped';
      return {
        ...state,
        stages: { ...state.stages, [event.stage]: 'done' },
        doneCount: wasSkipped ? state.doneCount : state.doneCount + 1,
        ...(duration !== undefined && {
          stageDurations: { ...state.stageDurations, [event.stage]: duration },
        }),
      };
    }
    case 'stage-error': {
      const startedAt = state.stageStarts[event.stage];
      const duration = startedAt ? now - startedAt : undefined;
      return {
        ...state,
        stages: { ...state.stages, [event.stage]: 'error' },
        errors: [...state.errors, { stage: event.stage, message: event.error }],
        ...(duration !== undefined && {
          stageDurations: { ...state.stageDurations, [event.stage]: duration },
        }),
      };
    }
    case 'pipeline-complete': {
      const lowYieldStages = (event.meta as { lowYieldStages?: StageId[] }).lowYieldStages ?? [];
      return { ...state, pipelineCompletedAt: now, lowYieldStages };
    }
    case 'workflow-started':
    case 'stage-progress':
      return state;
    case 'fatal': {
      const stage = state.currentStage ?? STAGE_IDS[STAGE_IDS.length - 1]!;
      return {
        ...state,
        currentStage: null,
        errors: [...state.errors, {
          stage,
          message: `[${event.code}] ${event.error}`,
        }],
      };
    }
  }
}

/** Format milliseconds as MM:SS (or SSs for short durations). */
export function formatElapsed(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
